import { GetStaticProps } from 'next';
import { NextSeo } from 'next-seo';
import dynamic from 'next/dynamic';
import React from 'react';

import { CDNAssets } from '../CDN';
import { ContactUsMode } from '../components/forms/ContactForm';
import AlternateLayoutList from '../components/layout/lists/AlternateLayoutList';
import HeaderSection, { HeaderSectionDestinationUrl } from '../components/layout/sections/HeaderSection';
import PageSection, { PageSectionType } from '../components/layout/sections/PageSection';
import PageSectionTitle from '../components/layout/sections/PageSectionTitle';
import { StrapiApiClient } from '../lib/integrations/strapi/StrapiApiClient';
import { StrapiApiScrollingLogo } from '../lib/integrations/strapi/types';
import { withGlobalDefaultStaticProps } from '../lib/next-hooks/withGlobalDefaultStaticProps';
import { getPageSeoConfig } from '../lib/seo/pageSeoConfigs';
import pageRoutes from '../router/pageRoutes';
import { NextPageWithLayout } from '../types/global-types';
import ScrollingLogosWidget from '../widgets/animated/ScrollingLogosWidget';
import BenefitsCards, { BenefitsCardsPageSectionWrapper } from '../widgets/cards/BenefitsCards';
import StepCards from '../widgets/cards/StepCards';
import ContactUsWidget from '../widgets/interactive/ContactUsWidget';
import FAQWidget from '../widgets/interactive/FAQWidget';

// Dynamically import with SSR disabled
const ScrollableTabPanels = dynamic(() => import('../components/interactive/ScrollableTabPanels'), { ssr: false });

type HowItWorksPageStaticProps = {
  scrollingLogos: StrapiApiScrollingLogo[];
};

export const getStaticProps: GetStaticProps<HowItWorksPageStaticProps> = async (context) => {
  const scrollingLogos = await StrapiApiClient.getAllScrollingLogos();
  const props: HowItWorksPageStaticProps = { scrollingLogos };
  return withGlobalDefaultStaticProps(context, { props });
};

const HowItWorksPage: NextPageWithLayout<HowItWorksPageStaticProps> = (props) => {
  const { scrollingLogos } = props;
  const seoConfig = getPageSeoConfig(pageRoutes.HowItWorks);
  return (
    <>
      <NextSeo {...seoConfig} />
      <HeaderSection
        subTitle="Gaspar AI"
        title="Employee helpdesk support reinvented"
        description="Let our AI helpdesk drive your workforce forward. Streamline processes, enhance employee engagement, and transform your support operations."
        destinationUrl={HeaderSectionDestinationUrl.RequestDemo}
      />
      <BenefitsCardsPageSectionWrapper>
        <BenefitsCards
          records={[
            {
              imageUrl: CDNAssets.getUrl('benefit-one-click-automation'),
              title: '1-click automation',
              description: 'Automate tasks effortlessly for streamlined productivity. No coding skills needed!',
            },
            {
              imageUrl: CDNAssets.getUrl('benefit-insights'),
              title: 'Employee & Business insights',
              description: 'Unlock data-driven insights for smarter decisions and optimized business operations.',
            },
            {
              imageUrl: CDNAssets.getUrl('benefit-human-like-answers'),
              title: 'Human-like instant answers',
              description: 'Get comprehensive answers for informed decision-making and troubleshooting.',
            },
          ]}
        />
      </BenefitsCardsPageSectionWrapper>
      <PageSection type={PageSectionType.White}>
        <AlternateLayoutList
          items={[
            {
              imageUrl: CDNAssets.getUrl('how-it-works-page-what-is-an-ai-helpdesk'),
              orientation: 'left',
              title: 'What is an AI helpdesk?',
              description:
                "A secure and always available support platform built for an AI-first world. Thanks to advanced language models, it offers dynamic, human-like conversations, seamlessly integrating with various communication channels and IT systems. It empowers employees to act, search, query, and generate content across a multitude of enterprise applications. Fueled by constantly improving generative models, AI-driven analytics for performance evaluation, and developer tools for customizable use cases, it's the future of assistance.",
            },
          ]}
        />
      </PageSection>
      <PageSection>
        <PageSectionTitle text="Employee helpdesk magic" />
        <ScrollableTabPanels
          renderPanel={(panel) => (
            <div className="max-w-5xl">
              <h2 className="text-5xl my-3">{(panel as any)?.title}</h2>
              <p className="text-lg">{(panel as any).description}</p>
            </div>
          )}
          panels={[
            {
              tabLabel: 'Task automation',
              data: {
                title: 'Task automation',
                description:
                  'Automatically solve 40% of routine issues and automate repetitive tasks, such as password resets, group management, software access, hardware provisioning, PTO requests – all without human intervention. Free up your employee support team and enable them to focus on creating impact through strategic projects. Our GenAI-powered automation platform resolves more than just L1 requests, across all your business systems.',
              },
            },
            {
              tabLabel: 'Instant answers',
              data: {
                title: 'Instant answers',
                description:
                  "Empower your workforce to instantly find the information they are looking for - HR policies, How-to questions, IT troubleshooting and more. Gaspar, our Generative AI Virtual Agent, searches articles, documents and data seamlessly across business systems and intranets. Employees simply engage in natural conversation with Gaspar, provide further clarifications if needed, and get quick, accurate answers. Now all your enterprise content is at your employees' fingertips via LLM-powered search!",
              },
            },
            {
              tabLabel: 'Personalized info',
              data: {
                title: 'Personalized info',
                description:
                  'Gaspar AI offers your employees quick and easy access to information tailored specifically to their needs and employment situation, such as their daily tasks and project deadlines, PTO balance, customers’ contract inquiries. Users can look up information that’s personalized and permissioned, saving time and boosting productivity.',
              },
            },
            {
              tabLabel: 'Approvals automation',
              data: {
                title: 'Approvals automation',
                description:
                  "Make it easy for everyone to get their PTO request approved or access the groups, folders, and calendars they need but not the ones they don't. The process happens on Slack or Microsoft Teams, and both the requester and the approver get status updates and notifications.",
              },
            },
            {
              tabLabel: 'AI Knowledge management',
              data: {
                title: 'AI Knowledge management',
                description:
                  'Integrate with your internal resources, repositories and knowledge bases to generate and offer accurate, human-like, concise answers to employee questions right within your company’s chat platform. The generated answers come with comprehensive source citations, ensuring the content’s reliability. You can also identify article gaps with AI recommendations based on real employee demand and create related content.',
              },
            },
            {
              tabLabel: 'Ticket automation',
              data: {
                title: 'Ticket automation',
                description:
                  'Automatically create and sync tickets with your ticketing system for issues resolved by our virtual Agent, so that you can keep track of your support operations. As for tickets created for requests that need resolution, our ML models analyze and categorize them so that they are routed to the right helpdesk agent in seconds. Employees simply fill in a form on Slack or MS Teams to create the ticket, and then receive status updates until the ticket is closed.',
              },
            },
            {
              tabLabel: 'Workflow automation',
              data: {
                title: 'Workflow automation',
                description:
                  'Reduce manual work by 80% and minimize repetitive tasks by creating automated workflows. Simplify onboarding, reduce offboarding time or create accounts easily by building no-code, customized workflows. You can activate them whenever needed, or set up triggers.',
              },
            },
            {
              tabLabel: 'Business Insights',
              data: {
                title: 'Business Insights',
                description:
                  'Uncover the most common employee issues, what’s hurting their productivity, which automations or content will unblock them. We use large language models to break down ticket data and present the areas of service that need the most improvement. Thus, you can identify which automation opportunities, process improvements and knowledge content will have the biggest business impact.',
              },
            },
            {
              tabLabel: 'Performance Metrics',
              data: {
                title: 'Performance Metrics',
                description:
                  'Measure your investment’s impact across the enterprise. Monitor which features, capabilities, and resources employees find valuable, and the performance of key KPIs. Dive deeper into issue clusters. Compare performance over time, make grounded decisions and take data-driven action.',
              },
            },
          ]}
        />
      </PageSection>
      <PageSection type={PageSectionType.White}>
        <PageSectionTitle text="How to get started" />
        <StepCards
          records={[
            {
              title: 'Request a demo or free trial',
              description: 'Fill in the forms below to request a demo or start a 21-day free trial.',
            },
            {
              title: 'Discuss your needs in a meeting',
              description: 'We will discuss your needs, show you the platform and answer any questions.',
            },
            {
              title: 'Deploy in 30 minutes',
              description: 'If our solution is a match, you can easily deploy it in 30 minutes.',
            },
          ]}
        />
      </PageSection>
      <PageSection isNarrow={true}>
        <ScrollingLogosWidget logos={scrollingLogos} />
      </PageSection>
      <PageSection isNarrowTop={true}>
        <ContactUsWidget defaultMode={ContactUsMode.FreeTrial} showAllModes={true} />
      </PageSection>
      <PageSection>
        <FAQWidget />
      </PageSection>
    </>
  );
};
HowItWorksPage.displayName = 'HowItWorksPage';
export default HowItWorksPage;
